import type {
	CertificationModuleShallow,
	RecommendedCoursePlanItem,
} from "@brookbush/types";

import clsx from "clsx";
import { ChevronRight } from "lucide-react";
import Link from "next/link";

import { useCertification } from "~/hooks";

interface CertificationModuleTitleProps {
	href: string;
	module: CertificationModuleShallow | RecommendedCoursePlanItem;
}

const CertificationModuleTitle = ({
	href,
	module,
}: CertificationModuleTitleProps) => {
	const { certification, isLoading } = useCertification();

	return (
		<Link
			href={href}
			className={clsx(
				"group mx-1 phablet:mx-0 flex phablet:w-auto w-full phablet:flex-row items-center justify-start phablet:justify-start gap-3 phablet:gap-2",
				{
					"animate-pulse": isLoading,
				},
			)}
		>
			<h2 className="group line-clamp-2 items-center font-medium phablet:font-semibold text-[1.2rem] leading-6 sm:text-2xl">
				{module.title}
			</h2>

			{!certification?.isStudyPlan && (
				<span
					className={clsx(
						"relative ml-auto phablet:ml-0 flex items-center justify-center whitespace-nowrap rounded-full px-2.5 py-1 font-semibold phablet:text-sm text-[0.8rem]",
						module.earnedCoreCredits &&
							module.earnedCoreCredits >= module.credits!
							? "bg-emerald-100 dark:bg-emerald-300/30 dark:text-slate-50"
							: module.slug === "electives"
								? "bg-purple-100 dark:bg-purple-300/30 dark:text-slate-50"
								: "bg-sky-100 dark:bg-sky-300/30 dark:text-slate-50",
					)}
				>
					{module.earnedCoreCredits}{" "}
					{module.earnedCoreCredits !== undefined && "/"} {module.credits}{" "}
					Credits
				</span>
			)}

			<ChevronRight className="-ml-1 inline-block h-5 phablet:h-auto phablet:w-auto w-5 shrink-0 text-slate-500 transition-all group-hover:translate-x-1 group-hover:scale-110 group-hover:text-slate-600 dark:text-slate-300 dark:group-hover:text-slate-400" />
		</Link>
	);
};

export default CertificationModuleTitle;
